import * as React from "react";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import { useToastStore, type Toast } from "@/lib/toast";

const AUTO_DISMISS_MS = 6000;

// MD3 snackbar: a single-line (or two-line) surface-inverse-toned surface,
// low-elevation, bottom-anchored. `success`/`destructive` tint the message
// text rather than the whole surface, per MD3 (snackbars don't have status
// colors — the content communicates status).
const variantStyles: Record<Toast["variant"], string> = {
  default: "text-foreground",
  success: "text-emerald-600 dark:text-emerald-400",
  destructive: "text-destructive",
};

function ToastCard({ toast }: { toast: Toast }) {
  const dismiss = useToastStore((s) => s.dismiss);
  React.useEffect(() => {
    const t = setTimeout(() => dismiss(toast.id), AUTO_DISMISS_MS);
    return () => clearTimeout(t);
  }, [toast.id, dismiss]);

  return (
    <div
      role="status"
      className="pointer-events-auto flex w-80 items-start gap-3 rounded-lg bg-surface-container-highest p-4 shadow-elevation-3"
    >
      <div className="flex-1 space-y-1">
        <p className={cn("text-sm font-medium", variantStyles[toast.variant])}>
          {toast.title}
        </p>
        {toast.description && (
          <p className="text-xs text-muted-foreground">{toast.description}</p>
        )}
      </div>
      <button
        onClick={() => dismiss(toast.id)}
        className="text-muted-foreground opacity-70 transition-opacity hover:opacity-100"
        aria-label="Dismiss"
      >
        <X className="size-4" />
      </button>
    </div>
  );
}

/** Renders active snackbars in a fixed stack. Mount once near the app root. */
export function Toaster() {
  const toasts = useToastStore((s) => s.toasts);
  return (
    <div className="pointer-events-none fixed bottom-4 right-4 z-50 flex flex-col gap-2">
      {toasts.map((t) => (
        <ToastCard key={t.id} toast={t} />
      ))}
    </div>
  );
}
